import { EMEARegion } from "@dana/Common/Constants";
import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import { fontWeights } from "@dana/MobiusOverrides/styles/typography";
import { formatDecimal } from "@dana/utils/utils";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LanguageModel, OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import CardContainer from "@insite/content-library/Components/CardContainer";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
    isEmea: useIsEmea(state),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderHistoryLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        gap: 10,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardContainer: {
        gap: 10,
    },

    orderLineCardGridContainer: {
        offsetCss: css`
            width: 100%;
            justify-content: space-between;
            flex-wrap: nowrap !important;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: row;
                        `,
                        css`
                            flex-direction: row;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        printWidth: 2,
        css: css`
            width: max-content !important;
            max-width: fit-content !important;

            @media print {
                max-height: 83px;
                max-width: 83px;
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [10, 10, 9, 9, 9],
        printWidth: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 3;
                            max-width: 100%;
                        `,
                        css`
                            order: 3;
                            max-width: 100%;
                        `,
                        css`
                            order: 2;
                        `,
                        css`
                            order: 2;
                        `,
                        css`
                            order: 2;
                        `,
                    ],
                    "min",
                )}
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
    },
    orderHistoryLineCardAddToListGridItem: {
        printWidth: 0,
        css: css`
            width: auto;
            max-width: max-content;
            flex-basis: max-content;
            justify-content: end;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 1;
                            position: absolute;
                            right: 8px;
                        `,
                        css`
                            order: 1;
                            position: absolute;
                            right: 8px;
                        `,
                        css`
                            order: 3;
                            position: relative;
                        `,
                        css`
                            order: 3;
                            position: relative;
                        `,
                        css`
                            order: 3;
                            position: relative;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoGridItem: {
        width: [12, 12, 3, 3, 3],
        printWidth: 3,
    },
    productInfoGridContainer: {
        gap: 16,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
    },
    productInfoErpNumberGridItem: {
        width: [12, 12, 12, 12, 12],

        css: css`
            padding: 0px 16px;
        `,
    },
    productInfoManufacturerItemGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoVmiBinNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoCustomerProductGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 12,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 9, 9, 9],
        printWidth: 9,
    },
    orderLineInfoGridContainer: {
        gap: 5,
    },
    orderLinePriceGridItem: {
        width: [3, 3, 3, 3, 3],

        css: css`
            flex-direction: column;
            gap: 12px;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    productInfoQtyOrderedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,

        css: css`
            flex-direction: column;
            gap: 12px;
            .qtySection {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }
        `,
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 4, 6, 6],
        printWidth: 4,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: [8, 8, 8, 8, 8],
        css: css`
            padding-top: 10px;
        `,
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoQtyOrderedHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    gap: 8px;
                `,
            },
            heading: {
                variant: "legend",
                weight: fontWeights.bold,
                css: css`
                    margin-bottom: 0px !important;
                `,
            },
        },
    },
    productInfoQtyShippedHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: fontWeights.bold,
                css: css`
                    margin-bottom: 0px !important;
                `,
            },
            text: {
                weight: fontWeights.bold,
            },

            wrapper: {
                css: css`
                    gap: 8px;
                `,
            },
        },
    },
    productInfoSubtotalHeadingAndText: {
        wrapper: {
            css: css`
                // padding-right: 10px;
            `,
        },
        heading: {
            variant: "legend",
            weight: fontWeights.bold,
        },

        text: {
            weight: fontWeights.bold,
        },
    },
    priceHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: fontWeights.bold,
                css: css`
                    margin-bottom: 0px !important;
                `,
            },
            text: {
                weight: fontWeights.bold,
            },

            wrapper: {
                css: css`
                    gap: 8px;
                `,
            },
        },
    },
    customerProductNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: fontWeights.bold,
            },
        },
    },
    erpNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: fontWeights.bold,
            },
        },
    },
    manufacturerHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: fontWeights.bold,
            },
        },
    },
    vmiBinNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: fontWeights.bold,
            },
        },
    },
};

const OrderLineProductInfo = ({
    orderLine,
    styles,
    vmiLocationId,    
}: {
    orderLine: OrderLineModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsText}>
                    {option.sectionName}: {option.optionName}
                </Typography>
            </li>
        ));
    }

    return (
        <GridItem {...styles.productInfoGridItem} className="descriptios">
            <GridContainer {...styles.productInfoGridContainer}>
                <GridItem {...styles.productInfoBrandDescriptionGridItem}>
                    <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                        {orderLine.brand && (
                            <GridItem {...styles.productInfoBrandGridItem}>
                                <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />
                            </GridItem>
                        )}
                        <GridItem {...styles.productInfoDescriptionGridItem}>
                            {vmiLocationId && orderLine.isActiveVmiBin && (
                                <VmiBinDetailTypeLink
                                    title={orderLine.shortDescription}
                                    locationId={vmiLocationId}
                                    id={orderLine.vmiBinId}
                                />
                            )}
                            {vmiLocationId && !orderLine.isActiveVmiBin && (
                                <Typography as="p" className="shortDescription">{orderLine.shortDescription}</Typography>
                            )}
                            {!vmiLocationId && (
                                <ProductDescription
                                    product={orderLine}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {sectionOptions.length > 0 && (
                    <GridItem {...styles.productInfoSectionOptionsGridItem}>
                        <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>
                    </GridItem>
                )}
                <GridItem {...styles.productInfoPartNumbersGridItem}>
                    <GridContainer {...styles.productInfoPartNumbersGridContainer}>
                        <GridItem {...styles.productInfoErpNumberGridItem}>
                            <Link
                                href={orderLine.productUri}
                                alt={`See product details for ${orderLine.productErpNumber}`}
                                data-test-selector="productDescriptionLink"
                            >
                                <SmallHeadingAndText
                                    {...styles.erpNumberHeadingAndText}
                                    heading={translate("")}
                                    text={orderLine.productErpNumber}
                                />
                            </Link>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    selectedLang : getSession(state).language,
    isEmea: useIsEmea(state),
});

const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
    showPricing,
    selectedLang,
    isEmea,
    }: {
    orderLine: OrderLineModel;
    order: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
    showPricing?: boolean;
    selectedLang?: LanguageModel|null;
    isEmea?: boolean;
}) => {
    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }

    const linestatus = orderLine.status !== "" && orderLine.status !== undefined ? JSON.parse(orderLine.status) : "";

    return (
        <GridItem {...styles.orderLineInfoGridItem} className="inforother">
            <GridContainer {...styles.orderLineInfoGridContainer}>
                <GridItem {...styles.orderLinePriceGridItem} width={12}>
                    <SmallHeadingAndText
                        {...styles.productInfoQtyOrderedHeadingAndText}
                        heading={translate("Line No.")}
                        data-test-selector="qty_Ordered"
                        text={`${orderLine.lineNumber}`}
                    />
                </GridItem>
                 <GridItem {...styles.productInfoQtyOrderedGridItem} width={12} className="mobileOnly">
                    <SmallHeadingAndText
                        {...styles.productInfoQtyOrderedHeadingAndText}
                        heading={translate("QTY Ordered")}
                        data-test-selector="qty_Ordered"
                        text={orderLine.qtyOrdered?.toLocaleString()}
                    />
                </GridItem>
                {showPricing && (
                    <GridItem {...styles.orderLinePriceGridItem} width={12}>
                        <SmallHeadingAndText
                            {...styles.priceHeadingAndText}
                            heading={translate("Price")}
                            text={
                                (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.unitPriceWithVatDisplay
                                    : orderLine.unitPriceDisplay) +
                                (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                            }
                        />
    
            {(selectedLang && selectedLang?.properties?.region!==EMEARegion) && (
             <>
                        <SmallHeadingAndText
                            {...styles.priceHeadingAndText}
                            heading={translate("Tariff Fee")}
                            text={orderLine.unitRegularPriceDisplay}
                        />
                        <SmallHeadingAndText
                            {...styles.priceHeadingAndText}
                            heading={translate("Core Deposit")}
                            text={orderLine.unitListPriceDisplay}
                        />
                        </>
                    )}
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.secondaryPriceText} as="p">
                                            {orderLine.unitPriceDisplay +
                                                (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")}
                                        </Typography>
                                        <Typography as="p" {...styles.secondaryVatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.productInfoQtyShippedGridItem} width={12}>
                    {linestatus !== "" &&
                        linestatus.map(
                            (item: { qtyShip: any; status: any; estShipDate: any; trackingNo: any; carrier: any,actShipDate:any }, index:any) => (
                                <StyledWrapper key={index+"lineNo"+orderLine.lineNumber} className="qtySection">
                                    <SmallHeadingAndText
                                        {...styles.productInfoQtyShippedHeadingAndText}
                                        heading={translate("Qty Shipped")}                                       
                                        text={
                                            item?.qtyShip?.toLocaleString() || "0"
                                          }
                                    />

                                    <SmallHeadingAndText
                                        {...styles.productInfoQtyShippedHeadingAndText}
                                        heading={translate("Status")}
                                        text={`${item?.status || ""}`}
                                    />
                                    <SmallHeadingAndText
                                        {...styles.productInfoQtyShippedHeadingAndText}
                                        heading={translate("Est. Ship Date")}
                                        text={`${item?.estShipDate || ""}`}
                                    />
                                    {item?.actShipDate && (
                                    <SmallHeadingAndText
                                        {...styles.productInfoQtyShippedHeadingAndText}
                                        heading={translate("Actual Ship Date")}
                                        text={`${item?.actShipDate || ""}`}
                                    />
                                    )}
                                    {item?.trackingNo && (
                                        <SmallHeadingAndText
                                            {...styles.productInfoQtyShippedHeadingAndText}
                                            heading={translate("Tracking No.")}
                                            text={`${item?.trackingNo || ""}`}
                                        />
                                    )}
                                    {item?.carrier && (
                                        <SmallHeadingAndText
                                            {...styles.productInfoQtyShippedHeadingAndText}
                                            heading={translate("Carrier")}
                                            text={`${item?.carrier || ""}`}
                                        />
                                    )}
                                </StyledWrapper>
                            ),
                        )}
                </GridItem>
                <GridItem {...styles.productInfoQtyOrderedGridItem} width={12} className="desktopOnly">
                    <SmallHeadingAndText
                        {...styles.productInfoQtyOrderedHeadingAndText}
                        heading={translate("QTY Ordered")}
                        data-test-selector="qty_Ordered"
                        text={orderLine.qtyOrdered?.toLocaleString()}
                    />
                </GridItem>
                <GridItem {...styles.productInfoQtyShippedGridItem} width={12}>
                    <SmallHeadingAndText
                        {...styles.productInfoQtyShippedHeadingAndText}
                        heading={translate("Weight")}
                        text={
                            isEmea
                              ? `${formatDecimal(orderLine.properties?.weight ?? 0, { locale: "fr-FR" })} ${orderLine.properties?.weightUom ?? ""}`
                              : `${formatDecimal(orderLine.properties?.weight ?? 0)} ${orderLine.properties?.weightUom ?? ""}`
                          }
                    />
                </GridItem>
                <GridItem {...styles.productInfoQtyShippedGridItem} width={12}>
                    <SmallHeadingAndText
                        {...styles.productInfoQtyShippedHeadingAndText}
                        heading={translate("Ship from Location(s)")}
                        text={`${orderLine?.warehouse}`}
                    />
                </GridItem>

                {showPricing && (
                    <GridItem {...styles.productInfoSubtotalGridItem} width={12}>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <SmallHeadingAndText
                                heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                text={orderLine.totalRegularPriceDisplay}
                                extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                            />
                        )}
                        <SmallHeadingAndText
                            heading={
                                !enableVat
                                    ? translate("Subtotal")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.totalRegularPriceDisplay
                                    : orderLine.totalRegularPriceDisplay
                            }
                            extendedStyles={styles.productInfoSubtotalHeadingAndText}
                        />
                    </GridItem>
                )}
            </GridContainer>
        </GridItem>
    );
};

const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: OrderLineNotesStyles;
}) => {
    return orderLine.notes ? (
        <GridItem {...extendedStyles?.gridItemStyles}>
            <Typography {...extendedStyles?.typographyStyles}>{orderLine.notes}</Typography>
        </GridItem>
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
}) => {
    const { orderLine, order, wishListSettings, vmiBinDetailsPageUrl, styles, setAddToListModalIsOpen, addToWishList } =
        props;
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const isShareEntity = getIsShareEntity();

    return (
        <GridContainer
            {...styles.orderLineCardGridContainer}
            data-test-selector={`orderLineCard${orderLine.productId}`}
        >
            <GridItem {...styles.orderLineCardImageGridItem} className="image-wrapper">
                {orderLine.productUri && orderLine.isActiveProduct && !order.vmiLocationId && (
                    <Link {...styles.productImageLink} href={orderLine.productUri}>
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    </Link>
                )}
                {orderLine.productUri && orderLine.isActiveProduct && order.vmiLocationId && orderLine.isActiveVmiBin && (
                    <Link
                        {...styles.productImageLink}
                        href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
                    >
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    </Link>
                )}
                {(!orderLine.isActiveProduct || !orderLine.productUri) && (
                    <LazyImage
                        {...styles.orderLineCardImage}
                        src={orderLine.mediumImagePath}
                        isShareEntity={isShareEntity}
                    />
                )}
            </GridItem>
            <GridItem {...styles.orderLineCardInfoGridItem} className="info-wrapper">
                <GridContainer {...styles.orderLineCardInfoGridContainer}>
                    <OrderLineProductInfo orderLine={orderLine} styles={styles} vmiLocationId={order.vmiLocationId} />
                    <OrderLineInfo orderLine={orderLine} order={order} styles={styles} />
                    <OrderLineNotes extendedStyles={styles.orderLineNotes} orderLine={orderLine} />
                </GridContainer>
            </GridItem>
            <GridItem {...styles.orderHistoryLineCardAddToListGridItem} className="addto-list-wrapper">
                <Hidden {...styles.orderLineCardActionsWide}>
                    {orderLine.canAddToWishlist && (
                        <OverflowMenu position="end" {...styles.overflowMenu}>
                            <Clickable {...styles.addToListClickable} onClick={addToListClickHandler}>
                                {translate("Add to List")}
                            </Clickable>
                        </OverflowMenu>
                    )}
                </Hidden>
                <Hidden {...styles.orderLineCardActionsNarrow}>
                    {orderLine.canAddToWishlist && (
                        <Button {...styles.orderLineCardAddToListButton} onClick={addToListClickHandler}>
                            {translate("Add to List")}
                        </Button>
                    )}
                </Hidden>
            </GridItem>
        </GridContainer>
    );
};
const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    vmiBinDetailsPageUrl,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    if (!order || !order.orderLines) {
        return null;
    }

    return (
        <GridContainer {...styles.gridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText}>{translate("Order Summary")}</Typography>
            </GridItem>
            {order.orderLines.map((orderLine, index) => (
                <CardContainer {...styles.orderLineCardContainer} key={orderLine.id+"container"+index}>
                    <GridItem {...styles.orderLineCardGridItem}>
                        <OrderLineCard
                            orderLine={orderLine}
                            order={order}
                            wishListSettings={wishListSettings}
                            vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                            styles={styles}
                            setAddToListModalIsOpen={setAddToListModalIsOpen}
                            addToWishList={addToWishList}
                        />
                    </GridItem>
                </CardContainer>
            ))}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
